/**
 * The key that wraps every refresh secret the web stores (I-1625, ADR-100's 2026-10-16 amendment).
 *
 * One AES-GCM 256 key per origin, made by WebCrypto with `extractable: false` and kept in
 * IndexedDB. The browser keeps the key material in its own key store: a copy of the profile on disk,
 * or a backup of it, holds only ciphertext. ⚠️ **This is not a defence against XSS.** A script
 * running in the page can ask this same key to unwrap. It closes reads of the disk and of backups,
 * and nothing more.
 *
 * ⛔ **Only `refreshSecretWriteAhead.ts` calls this** (and its tests). That module is the one place that reads and
 * writes the refresh slots, and it decides what a failure means.
 *
 * ⛔ **The desktop app keeps no key at all** (D-0315 a, I-1696). Storing or reading a `CryptoKey` in
 * IndexedDB makes WebKit fetch its "<app> WebCrypto Master Key" from the person's login keychain with
 * a synchronous call on the page's thread, and a locked or missing keychain froze the page blank
 * behind it; no timer can interrupt that call. In the desktop runtime every slot is held in page
 * memory only, and the native keychain, written through the app's own async IPC
 * (`desktopSessionVault.ts`, `nativeSessionMirror.ts`), is the one durable copy. The browser keeps
 * the vault unchanged.
 */

import { isDesktopRuntime } from '@/desktop/desktopDetection';

/** The IndexedDB database, store and record that hold the key. Per origin, never per person. */
export const VAULT_DATABASE = 'featit-secret-vault';
const VAULT_STORE = 'keys';
const VAULT_KEY_ID = 'refresh-secret-wrap';

/** What a wrapped value starts with. No plain refresh secret can start this way: base64url has no `.`. */
export const WRAPPED_PREFIX = 'vault1.';

/**
 * jsdom has no IndexedDB, so the test setup hands this module one under this symbol
 * (`test/fakeIndexedDb.ts`). A browser never sets it, and the browser's own `indexedDB` is used.
 */
export const VAULT_INDEXED_DB_OVERRIDE = Symbol.for('refreshSecretVault:indexedDB');

/** No key can be had here: no IndexedDB, no WebCrypto, or a store that refused us. */
export class VaultUnavailableError extends Error {
  // Declared here: the web's `lib` predates `Error.cause` (ES2022).
  readonly cause: unknown;

  constructor(cause?: unknown) {
    super('The refresh secret vault is not available in this browser.');
    this.name = 'VaultUnavailableError';
    this.cause = cause;
  }
}

/** A wrapped value that did not unwrap: the key is gone or was replaced, or the value is damaged. */
export class RefreshSecretUnreadableError extends Error {
  // Declared here: the web's `lib` predates `Error.cause` (ES2022).
  readonly cause: unknown;

  constructor(cause?: unknown) {
    super('A stored refresh secret could not be read.');
    this.name = 'RefreshSecretUnreadableError';
    this.cause = cause;
  }
}

function vaultIndexedDb(): IDBFactory | undefined {
  const override = (globalThis as Record<symbol, unknown>)[VAULT_INDEXED_DB_OVERRIDE];
  if (override) return override as IDBFactory;
  return typeof indexedDB === 'undefined' ? undefined : indexedDB;
}

function subtle(): SubtleCrypto | undefined {
  return globalThis.crypto?.subtle;
}

/** Whether this page can have a vault at all. Asked before anything is moved into memory. */
export function vaultAvailable(): boolean {
  const crypto = subtle();
  return (
    !keepsNoKey() &&
    Boolean(vaultIndexedDb()) &&
    typeof crypto?.generateKey === 'function' &&
    typeof crypto?.encrypt === 'function'
  );
}

function openVault(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(VAULT_DATABASE, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(VAULT_STORE)) db.createObjectStore(VAULT_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('The vault database is blocked.'));
  });
}

function isAesGcmKey(value: unknown): value is CryptoKey {
  const key = value as Partial<CryptoKey> | null | undefined;
  return (
    typeof key === 'object' &&
    key !== null &&
    key.type === 'secret' &&
    (key.algorithm as { name?: unknown } | undefined)?.name === 'AES-GCM'
  );
}

/**
 * The key, or the candidate if no key was there yet: one `readwrite` transaction that reads, and
 * adds only when it found nothing.
 *
 * ⭐ IndexedDB runs `readwrite` transactions on one store one at a time across every tab of the
 * origin, so the read and the add cannot be split by another tab. Two tabs that race both come out
 * holding the first key that was written (VA-5).
 *
 * ⛔ *Mutation:* `put` the candidate without reading first → `refreshSecretVault.test.ts` goes red:
 * each tab keeps its own key, and one cannot unwrap what the other wrote.
 */
function keepFirstKey(db: IDBDatabase, candidate: CryptoKey | null): Promise<CryptoKey | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(VAULT_STORE, candidate ? 'readwrite' : 'readonly');
    const store = tx.objectStore(VAULT_STORE);
    let chosen: CryptoKey | null = null;
    const read = store.get(VAULT_KEY_ID);
    read.onsuccess = () => {
      if (isAesGcmKey(read.result)) {
        chosen = read.result;
      } else if (candidate) {
        store.add(candidate, VAULT_KEY_ID);
        chosen = candidate;
      }
    };
    tx.oncomplete = () => resolve(chosen);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function loadOrCreateKey(): Promise<CryptoKey> {
  const factory = vaultIndexedDb();
  const crypto = subtle();
  if (!factory || !crypto) throw new VaultUnavailableError();
  let db: IDBDatabase;
  try {
    db = await openVault(factory);
  } catch (error) {
    throw new VaultUnavailableError(error);
  }
  try {
    const existing = await keepFirstKey(db, null);
    if (existing) return existing;
    const candidate = await crypto.generateKey({ name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt',
    ]);
    const kept = await keepFirstKey(db, candidate);
    if (!kept) throw new Error('The vault kept no key.');
    return kept;
  } catch (error) {
    throw new VaultUnavailableError(error);
  } finally {
    db.close();
  }
}

let keyPromise: Promise<CryptoKey> | null = null;

/**
 * RES-13c (I-1696): how long a write waits for the key before the page stops asking. Storing a
 * `CryptoKey` makes WebKit fetch its "WebCrypto Master Key" from the person's default keychain; a
 * locked keychain waits on a dialog that may never be answered. A current secret is then kept in
 * memory (§8). The desktop never gets here: it asks for no key at all (D-0315 a, `keepsNoKey`).
 */
export const VAULT_KEY_TIMEOUT_MS = 5_000;

/** Set once a write gave up on the key: this page asks for it no more, and keeps secrets in memory. */
let keyStalled = false;

type VaultFallbackListenerType = () => void;
const fallbackListeners = new Set<VaultFallbackListenerType>();
let fellBack = false;

/**
 * Called once a current secret is kept in memory only because the vault would not take it. For the
 * desktop's one-line notice (`desktop/restoringSession.tsx`); a page with no IndexedDB at all is
 * told too, and decides itself whether to say anything.
 */
export function subscribeToVaultFallback(listener: VaultFallbackListenerType): () => void {
  fallbackListeners.add(listener);
  return () => fallbackListeners.delete(listener);
}

/** Whether this page has kept a current secret in memory only because the vault would not. */
export function vaultFellBack(): boolean {
  return fellBack;
}

function announceFallback(): void {
  if (fellBack) return;
  fellBack = true;
  for (const listener of fallbackListeners) listener();
}

/** The key for a write: given up on after `VAULT_KEY_TIMEOUT_MS`, and not asked for again then. */
function wrappingKeyForWrite(): Promise<CryptoKey> {
  if (keyStalled)
    return Promise.reject(new VaultUnavailableError(new Error('the key store stalled')));
  let timer: ReturnType<typeof setTimeout> | undefined;
  const stalled = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      keyStalled = true;
      reject(new VaultUnavailableError(new Error('the key store did not answer')));
    }, VAULT_KEY_TIMEOUT_MS);
  });
  return Promise.race([wrappingKey(), stalled]).finally(() => clearTimeout(timer));
}

/**
 * D-0315 a: the desktop never asks for the key, so WebKit never touches the keychain. Checked before
 * anything opens IndexedDB, so even a wrapped value left on disk by an older build is not unwrapped.
 * ⛔ *Mutation:* drop this check → `refreshSecretVault.desktop.test.ts` goes red: the key store is
 * opened.
 */
function keepsNoKey(): boolean {
  return isDesktopRuntime();
}

/** This origin's key, loaded once per page. A failure is not cached: the next call tries again. */
function wrappingKey(): Promise<CryptoKey> {
  if (keepsNoKey()) {
    return Promise.reject(new VaultUnavailableError(new Error('the desktop keeps no key')));
  }
  keyPromise ??= loadOrCreateKey().catch((error: unknown) => {
    keyPromise = null;
    throw error;
  });
  return keyPromise;
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  const base64 = text.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

/** Whether a stored value is wrapped. Anything else in a refresh slot is a plain secret. */
export function isWrappedRefreshSecret(stored: string): boolean {
  return stored.startsWith(WRAPPED_PREFIX);
}

/**
 * Wrap `plain` for `slot`. The slot is the additional authenticated data, so a value cannot be moved
 * to another slot and still unwrap. Throws `VaultUnavailableError` when there is no key.
 */
export async function wrapRefreshSecret(plain: string, slot: string): Promise<string> {
  const key = await wrappingKeyForWrite();
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  try {
    const sealed = await globalThis.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(slot) },
      key,
      new TextEncoder().encode(plain),
    );
    return `${WRAPPED_PREFIX}${toBase64Url(iv)}.${toBase64Url(new Uint8Array(sealed))}`;
  } catch (error) {
    throw new VaultUnavailableError(error);
  }
}

/** Unwrap a value `wrapRefreshSecret` made for `slot`. Throws `RefreshSecretUnreadableError`. */
export async function unwrapRefreshSecret(stored: string, slot: string): Promise<string> {
  const [iv, sealed, ...rest] = stored.slice(WRAPPED_PREFIX.length).split('.');
  if (!iv || !sealed || rest.length > 0) throw new RefreshSecretUnreadableError();
  let key: CryptoKey;
  try {
    key = await wrappingKey();
  } catch (error) {
    throw new RefreshSecretUnreadableError(error);
  }
  try {
    const opened = await globalThis.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64Url(iv), additionalData: new TextEncoder().encode(slot) },
      key,
      fromBase64Url(sealed),
    );
    return new TextDecoder().decode(opened);
  } catch (error) {
    throw new RefreshSecretUnreadableError(error);
  }
}

/*
 * ── The slots ───────────────────────────────────────────────────────────────────────────────────
 *
 * A slot is a `localStorage` key that holds one refresh secret, or one string standing for several
 * (the write-ahead candidates as JSON). Every read and write of a slot goes through these, and
 * nothing in a slot is ever plain once a write has gone through here.
 */

/** Slots this page holds in memory only, because there is no vault (ADR-100 I-1625 §8). */
const memoryOnly = new Map<string, string>();

/** Writes queued in this page, in order. A read waits for every write queued before it. */
let queue: Promise<void> = Promise.resolve();
/** Per slot: bumped by `removeStoredRefreshSecret`, so a write queued before a removal is dropped. */
const generations = new Map<string, number>();
/**
 * Per slot: the plain value of the newest write queued and not yet settled, for sign-out's
 * synchronous peek (`heldInMemoryRefreshSecret`). Kept only while the write is being wrapped; it is
 * the value the caller already holds in memory, never anything read back from disk.
 */
const queuedPlain = new Map<string, { plain: string; generation: number; write: object }>();

function generationOf(slot: string): number {
  return generations.get(slot) ?? 0;
}

function enqueue<T>(write: () => Promise<T>): Promise<T> {
  const run = queue.then(write, write);
  queue = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/** The store refused the wrapped value, or there is no vault for a value that must be durable. */
export class RefreshSecretNotStoredError extends Error {
  // Declared here: the web's `lib` predates `Error.cause` (ES2022).
  readonly cause: unknown;

  constructor(cause?: unknown) {
    super('The refresh secret could not be stored wrapped.');
    this.name = 'RefreshSecretNotStoredError';
    this.cause = cause;
  }
}

/** Write `wrapped` to `slot` and prove it is there. */
function setAndVerify(slot: string, wrapped: string): void {
  localStorage.setItem(slot, wrapped);
  if (localStorage.getItem(slot) !== wrapped) throw new RefreshSecretNotStoredError();
}

/**
 * Whether `slot` holds a secret, answered synchronously: on disk, in memory, or in a write still
 * being wrapped. For the presence checks that cannot wait (`hasStoredAuthSession`, `hadCredential`).
 */
export function holdsStoredRefreshSecret(slot: string): boolean {
  // A write a removal has dropped is not held: a sign-out mid-sign-in reads as signed out.
  if (heldInMemoryRefreshSecret(slot) !== null) return true;
  try {
    const stored = localStorage.getItem(slot);
    // D-0315 a: a wrapped value from an older desktop build cannot be read here, so it holds
    // nothing, and the start restores the session from the native keychain instead.
    if (stored && keepsNoKey() && isWrappedRefreshSecret(stored)) return false;
    return Boolean(stored);
  } catch {
    return false;
  }
}

/**
 * The plain secret in `slot`, or `null` when it holds none. Waits for this page's queued writes.
 *
 * - **Wrapped** → unwrapped. A value that will not unwrap throws `RefreshSecretUnreadableError`, and
 *   the caller ends the session so the person signs in again (§4). It is never `null`, because a
 *   `null` reads as "never signed in" and would hide why the session ended.
 * - **Plain** (from before I-1625) → wrapped and written back in place of the plain copy,
 *   compare-and-set against what was read, then returned (§3). With no vault the plain copy is moved
 *   to memory and removed from disk (§8).
 */
export async function readStoredRefreshSecret(slot: string): Promise<string | null> {
  await queue;
  const remembered = memoryOnly.get(slot);
  if (remembered !== undefined) return remembered;
  let stored: string | null;
  try {
    stored = localStorage.getItem(slot);
  } catch (error) {
    throw new RefreshSecretUnreadableError(error);
  }
  if (!stored) return null;
  if (isWrappedRefreshSecret(stored)) {
    if (keepsNoKey()) {
      // D-0315 a: unreadable without the key the desktop no longer asks for; never unwrapped.
      localStorage.removeItem(slot);
      return null;
    }
    return unwrapRefreshSecret(stored, slot);
  }

  // A plain copy from before the vault: wrap it now, and never leave it behind.
  const plain = stored;
  await enqueue(async () => {
    if (localStorage.getItem(slot) !== plain) return;
    try {
      const wrapped = await wrapRefreshSecret(plain, slot);
      // ⛔ Compare-and-set, checked again after the await and right before the write: another tab
      // may have rotated the secret while this one was wrapping, and writing the old secret back
      // over its successor would undo that rotation. *Mutation:* drop this check →
      // `refreshSecretVault.test.ts` and `ApiService.sessionRace.test.ts` go red.
      if (localStorage.getItem(slot) !== plain) return;
      setAndVerify(slot, wrapped);
    } catch {
      if (localStorage.getItem(slot) !== plain) return;
      memoryOnly.set(slot, plain);
      localStorage.removeItem(slot);
    }
  });
  return plain;
}

/**
 * Store `plain` in `slot`, wrapped. Resolves once it is on disk (or, with `durable: false` and no
 * vault, in memory). Queued behind this page's earlier writes, so reads after it see it.
 *
 * - `durable: true` — for a write-ahead candidate, which only counts if it outlives the page:
 *   rejects with `RefreshSecretNotStoredError` when there is no vault or the store refuses. Nothing
 *   is kept anywhere then, and the caller must not name the secret to the server.
 * - `durable: false` — for the current secret: never rejects. With no vault it is kept in memory for
 *   this page, and nothing is written to disk.
 *
 * A `removeStoredRefreshSecret(slot)` called after this was queued drops the write.
 */
export function writeStoredRefreshSecret(
  slot: string,
  plain: string,
  options: { durable: boolean; notBefore?: Promise<unknown> },
): Promise<void> {
  const generation = generationOf(slot);
  const write = {};
  queuedPlain.set(slot, { plain, generation, write });
  const settle = () => {
    if (queuedPlain.get(slot)?.write === write) queuedPlain.delete(slot);
  };
  return enqueue(async () => {
    // RES-13b: a write the start makes waits for the first paint; the slot reads as held meanwhile.
    if (options.notBefore) await options.notBefore.catch(() => undefined);
    if (keepsNoKey()) {
      // D-0315 a: the desktop holds every slot in memory, by design rather than as a fallback, so
      // nothing is announced. A durable write resolves too: its durable home is the native keychain,
      // which `rotateWriteAhead` writes and awaits (`mirrorRefreshChain`) before naming a candidate.
      if (generationOf(slot) !== generation) return;
      memoryOnly.set(slot, plain);
      try {
        localStorage.removeItem(slot);
      } catch {
        // Nothing on disk can be removed from a store that cannot be written.
      }
      return;
    }
    let wrapped: string;
    try {
      wrapped = await wrapRefreshSecret(plain, slot);
    } catch (error) {
      if (options.durable) throw new RefreshSecretNotStoredError(error);
      if (generationOf(slot) !== generation) return;
      memoryOnly.set(slot, plain);
      announceFallback();
      try {
        localStorage.removeItem(slot);
      } catch {
        // Nothing on disk can be removed from a store that cannot be written.
      }
      return;
    }
    if (generationOf(slot) !== generation) return;
    try {
      setAndVerify(slot, wrapped);
      memoryOnly.delete(slot);
    } catch (error) {
      if (options.durable) throw new RefreshSecretNotStoredError(error);
      memoryOnly.set(slot, plain);
      try {
        localStorage.removeItem(slot);
      } catch {
        // As above.
      }
    }
  }).finally(settle);
}

/** Forget `slot` now, on disk and in memory, and drop any write to it still being wrapped. */
export function removeStoredRefreshSecret(slot: string): void {
  generations.set(slot, generationOf(slot) + 1);
  memoryOnly.delete(slot);
  queuedPlain.delete(slot);
  try {
    localStorage.removeItem(slot);
  } catch {
    // A store that cannot be written holds nothing this could remove.
  }
}

/**
 * The plain value this page holds for `slot` **in memory only**, synchronously: the newest write
 * still being wrapped, or else the memory slot of a page with no vault (§8). `null` otherwise.
 *
 * ⛔ **Never reads the disk.** It exists for sign-out, which must name every secret the page holds
 * before it clears the slots in the same tick (`captureHeldRefreshSecrets`): a wrapped value on disk
 * is read raw there and unwrapped after, and this covers what the disk does not hold yet, or ever.
 */
export function heldInMemoryRefreshSecret(slot: string): string | null {
  const queued = queuedPlain.get(slot);
  if (queued && queued.generation === generationOf(slot)) return queued.plain;
  return memoryOnly.get(slot) ?? null;
}
